import type { FC, FormEvent } from 'react';
import { useEffect, useState } from 'react';

import useDispatch from '@app/hooks/useDispatch';
import useEffectOnce from '@app/hooks/useEffectOnce';
import useSelector from '@app/hooks/useSelector';

import { handleErrorInUrl } from '../../helpers';
import { loginSelector } from '../../selectors';
import type { Network } from '../../types';
import ForgetPassModal from './ForgetPassModal';
import type { ResetModal, ResetModalData } from './LinkSentModal';
import LinkSentModal from './LinkSentModal';
import LoginPage from './LoginPage';
import { loginActions } from './reducers';
import type { LoginFormDataType } from './types';

interface Props {
    fromUri: string;
    oauthCode: string;
    email: string;
    projectId: string;
    lang: string;
    resetModal: ResetModal;
    buttonText: string;
}

const LoginPageContainer: FC<Props> = ({ fromUri, email, projectId, lang, resetModal, buttonText }) => {
    const dispatch = useDispatch();
    const { formData, loading, error } = useSelector(loginSelector);
    const [resetPassModal, setResetPassModal] = useState(false);
    const [linkSentModal, setLinkSentModal] = useState(false);
    const [resetData, setResetData] = useState<ResetModalData | undefined>(undefined);

    useEffectOnce(() => {
        handleErrorInUrl();
    });

    const handleDataChange = (data: LoginFormDataType) => {
        dispatch(loginActions.updateFormData(data));
    };

    useEffect(() => {
        handleDataChange({ email, joinProject: projectId, lang });
    }, [email, projectId, lang]);

    const handleNetworkClick = (network: Network) => {
        dispatch(loginActions.networkLogin(network, fromUri));
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (loading) {
            return;
        }
        dispatch(loginActions.login());
    };

    return (
        <>
            <LoginPage
                buttonText={buttonText}
                loading={loading}
                formData={formData}
                error={error}
                onNetworkClick={handleNetworkClick}
                onFormDataChange={handleDataChange}
                onSubmit={handleSubmit}
                onResetPassClick={() => setResetPassModal(true)}
            />
            {resetPassModal && (
                <ForgetPassModal
                    onClose={() => setResetPassModal(false)}
                    onSuccess={(data) => {
                        setResetData(data);
                        setResetPassModal(false);
                        setLinkSentModal(true);
                    }}
                />
            )}
            {linkSentModal && (
                <LinkSentModal
                    onClose={() => setLinkSentModal(false)}
                    resetModal={{ ...resetModal, data: resetData }}
                />
            )}
        </>
    );
};

export default LoginPageContainer;
